/* ═══════════════════════════════════════════════════════
   CoreFlow Twilio Browser Calling — Production
   Uses @twilio/voice-sdk (npm package)
   ═══════════════════════════════════════════════════════ */

import { Device } from "@twilio/voice-sdk";
import apiFetch from './api';
import { logCall } from './callLogger';
import { supabase } from './supabase';

const TOKEN_URL = "/api/twilio/token";

let device = null;
let activeCall = null;
let callStartTime = null;
let listeners = {};
// Tracked across makeCall lifecycle so error/catch paths can still
// report which number was being dialed when activeCall is null
// (synchronous catch fires before activeCall ever assigns).
let lastDialedNumber = null;

// ─── Token refresh state ───
// _identity is lifted from initDevice's local scope so refreshToken can
// use it without re-deriving from userEmail. _tokenIssuedAt anchors the
// backstop timer + visibility-recovery threshold. The two in-flight
// flags prevent concurrent refresh storms (proactive backstop + SDK's
// tokenWillExpire + reactive recovery can all race otherwise).
let _identity = null;
let _tokenIssuedAt = null;
let _backstopTimer = null;
let _refreshInFlight = false;
let _reactiveRetryInFlight = false;
let _visibilityListenerAttached = false;
// Set by makeCall on entry, cleared in its finally block. Prevents the
// "Call already initiating" race where a second makeCall fires while the
// first's device.connect() is still in flight (~200-2000ms). Common
// trigger: rep clicks the Call button during the transition window after
// autoDialNext has already fired the next call. Without this guard the
// second device.connect() throws InvalidStateError / "Call already
// active" and the queue advances on a failed call_logs row.
let _makeCallInFlight = false;

const BACKSTOP_MS = 45 * 60 * 1000;            // refresh at 45min — 15-min buffer (was 50min/10-min) before 60-min hard expiry
const STALE_ON_VISIBILITY_MS = 45 * 60 * 1000; // tab-return: force refresh if older than this (matches BACKSTOP_MS)

// ─── Event System ───
export function onCallEvent(event, callback) {
  if (!listeners[event]) listeners[event] = [];
  listeners[event].push(callback);
}

export function offCallEvent(event, callback) {
  if (!listeners[event]) return;
  listeners[event] = listeners[event].filter((cb) => cb !== callback);
}

function emit(event, data) {
  (listeners[event] || []).forEach((cb) => cb(data));
}

// ─── Token refresh helper (proactive + reactive paths share this) ───
// Single in-flight guard means proactive backstop, SDK tokenWillExpire,
// visibility-recovery, and reactive 31205 retry can all call this
// safely — concurrent callers see the no-op short-circuit.
//
// Backoff schedule: immediate, then 5s, 15s, 60s. Total max wait if all
// fail: ~80s. Replaces the previous one-shot +5s retry which gave up
// permanently on transient failures.
async function refreshToken(reason = "scheduled") {
  if (_refreshInFlight) {
    console.log("[CoreFlow Dialer] Refresh already in flight, skipping (" + reason + ")");
    return false;
  }
  if (!device || !_identity) {
    console.warn("[CoreFlow Dialer] Cannot refresh: device or identity not initialized");
    return false;
  }

  _refreshInFlight = true;
  const backoffSchedule = [0, 5000, 15000, 60000];

  try {
    for (let attempt = 0; attempt < backoffSchedule.length; attempt++) {
      if (attempt > 0) {
        await new Promise((r) => setTimeout(r, backoffSchedule[attempt]));
        console.log("[CoreFlow Dialer] Refresh retry attempt " + (attempt + 1) + "...");
      }

      try {
        const r = await apiFetch(TOKEN_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ identity: _identity, orgId: window.__coreflow_org_id }),
        });
        const d = await r.json();

        // Gap A fix: treat falsy d.token as failure instead of silently
        // succeeding into nothing. Previously a {error:"..."} response
        // (e.g. verifyAuth rejection) would log nothing and emit no
        // health signal — user kept a stale token until they hit 31205.
        if (!d.token) {
          console.warn("[CoreFlow Dialer] Token endpoint returned no token (attempt " + (attempt + 1) + "):", d.error || "unknown");
          continue;
        }

        device.updateToken(d.token);
        _tokenIssuedAt = Date.now();
        scheduleBackstop();

        console.log("[CoreFlow Dialer] ✅ Token refreshed (" + reason + ", attempt " + (attempt + 1) + ")");
        emit("health", { status: "connected" });
        return true;
      } catch (e) {
        console.warn("[CoreFlow Dialer] Token fetch attempt " + (attempt + 1) + " failed:", e?.message || e);
      }
    }

    console.error("[CoreFlow Dialer] Token refresh failed after " + backoffSchedule.length + " attempts");
    emit("health", { status: "disconnected" });
    return false;
  } finally {
    _refreshInFlight = false;
  }
}

// Backstop timer — fires at 50 minutes from token issuance, independent
// of Twilio's internal tokenWillExpire (which uses the SDK's setTimeout
// and is subject to background-tab throttling). Both can fire safely;
// the second hits refreshToken's in-flight guard.
function scheduleBackstop() {
  if (_backstopTimer) {
    clearTimeout(_backstopTimer);
    _backstopTimer = null;
  }
  _backstopTimer = setTimeout(() => {
    console.log("[CoreFlow Dialer] Backstop timer firing — proactively refreshing");
    refreshToken("backstop");
  }, BACKSTOP_MS);
}

// On tab return after being backgrounded for an extended period, both
// the SDK's tokenWillExpire timer AND our backstop setTimeout can be
// throttled past the 60-min hard expiry. Belt-and-braces: check token
// age explicitly when the tab comes back to foreground.
function handleVisibilityChange() {
  if (document.visibilityState !== "visible") return;
  if (!_tokenIssuedAt) return;
  const ageMs = Date.now() - _tokenIssuedAt;
  if (ageMs >= STALE_ON_VISIBILITY_MS) {
    console.log("[CoreFlow Dialer] Tab returned to foreground with stale token (" + Math.round(ageMs / 60000) + "min old) — forcing refresh");
    refreshToken("visibility-recovery");
  }
}

// ─── Initialize Twilio Device ───
export async function initDevice(userEmail, orgId) {
  // Skip if already initialized and registered
  if (device && device.state === "registered") {
    console.log("[CoreFlow Dialer] Device already registered, skipping init");
    emit("ready", {});
    return { success: true };
  }

  try {
    // Build a Twilio-safe identity from user email
    const identity = userEmail
      ? userEmail.replace(/@/g, "_at_").replace(/\./g, "_").replace(/[^a-zA-Z0-9_]/g, "")
      : "agent";
    // Lift to module scope so refreshToken (proactive backstop, SDK
    // tokenWillExpire, visibility recovery, reactive recovery) can
    // re-issue without re-deriving from userEmail.
    _identity = identity;

    console.log("[CoreFlow Dialer] Initializing for:", identity);

    const resp = await apiFetch(TOKEN_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ identity, orgId: window.__coreflow_org_id }),
    });

    if (!resp.ok) throw new Error("Token fetch failed: " + resp.status);

    const data = await resp.json();
    if (!data.token) throw new Error("No token in response");

    // Destroy existing device
    if (device) {
      try { device.destroy(); } catch (e) { /* ignore */ }
      device = null;
    }

    device = new Device(data.token, {
      edge: "ashburn",
      closeProtection: true,
      logLevel: "warn",
      allowIncomingWhileBusy: false,
      enableImprovedSignalingErrorPrecision: true,
      // SDK default is 10s — way too tight when a refresh round-trip can
      // exceed that on a flaky network. 5 minutes gives the refresh ample
      // time and pairs with the 45-min backstop for layered coverage.
      tokenRefreshMs: 5 * 60 * 1000,
    });

    // Anchor backstop timer + visibility threshold to this issuance.
    _tokenIssuedAt = Date.now();
    scheduleBackstop();

    device.on("registered", () => {
      console.log("[CoreFlow Dialer] ✅ Device registered");
      emit("ready", { status: "ready", identity: data.identity });
    });

    device.on("unregistered", () => emit("unregistered", {}));

    device.on("error", (twilioError) => {
      console.error("[CoreFlow Dialer] Device error:", twilioError.code, twilioError.message);
      // Token expired (31205) or invalid (31204) surfaced via the device's
      // async error channel — the synchronous makeCall catch can't see this
      // path. Fire a refresh; no per-call retry at this layer because the
      // next outbound call picks up the new token automatically via
      // refreshToken's device.updateToken() side-effect.
      if (twilioError?.code === 31205 || twilioError?.code === 31204) {
        console.log("[CoreFlow Dialer] Token error on device, refreshing");
        refreshToken("device-error").catch(e =>
          console.error("[CoreFlow Dialer] device-error refresh failed:", e?.message)
        );
      }
      emit("error", { message: twilioError.message, code: twilioError.code });
    });

    device.on("incoming", (call) => {
      activeCall = call;
      emit("incoming", { from: call.parameters.From });
    });

    // SDK signal — kept alongside our backstop timer. Both can fire safely;
    // the second hits refreshToken's in-flight guard. Background-tab
    // throttling can delay this event past hard expiry, which is exactly
    // why scheduleBackstop() exists as a deterministic deadline.
    device.on("tokenWillExpire", () => {
      console.log("[CoreFlow Dialer] tokenWillExpire fired");
      refreshToken("tokenWillExpire");
    });

    device.on("unregistered", () => {
      console.warn("[CoreFlow Dialer] ⚠️ Device unregistered");
      emit("health", { status: "disconnected" });
    });

    await device.register();

    // Visibility recovery — covers the case where both the SDK's
    // tokenWillExpire AND our backstop got throttled while the tab was
    // backgrounded. On tab return, refreshToken() runs if the existing
    // token is older than STALE_ON_VISIBILITY_MS. Listener guard
    // prevents double-attachment under React StrictMode double-mount.
    if (!_visibilityListenerAttached && typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      _visibilityListenerAttached = true;
    }

    return { success: true, identity: data.identity };
  } catch (err) {
    console.error("[CoreFlow Dialer] Init failed:", err?.message);
    emit("error", { message: err?.message || String(err) });
    return { success: false, error: err?.message || String(err) };
  }
}

// ─── Make Outbound Call ───
// Threshold matches BACKSTOP_MS — if the backstop got throttled by a
// background-tab inactivity window, this catches the case at the point
// of actual call activity. Adds ~200-500ms to at most one call per hour.
const TOKEN_AGE_THRESHOLD_MS = 45 * 60 * 1000;

export async function makeCall(phoneNumber) {
  if (!device) {
    return { success: false, error: "Device not initialized. Click Initialize Dialer first." };
  }

  // Race guard — see _makeCallInFlight comment above. Second concurrent
  // makeCall short-circuits without touching the Twilio device. Caller
  // gets a soft rejection; UI-level dialing-state guard in DialerPage
  // should prevent the click in the first place, but this is the
  // SDK-level backstop covering programmatic paths too (autoDialNext,
  // voice-command auto-dial, redial).
  if (_makeCallInFlight) {
    console.warn("[CoreFlow Dialer] makeCall already in flight, ignoring duplicate to " + phoneNumber);
    return { success: false, error: "Call already initiating" };
  }
  _makeCallInFlight = true;

  try {
  // Pre-call age check. If the token is older than the threshold (e.g.
  // backstop setTimeout was throttled while the tab was occluded),
  // refresh before issuing device.connect(). Refresh failures fall
  // through to the existing reactive-recovery paths (sync catch +
  // async call.on("error") wired above) rather than blocking the call.
  if (_tokenIssuedAt && Date.now() - _tokenIssuedAt > TOKEN_AGE_THRESHOLD_MS) {
    const ageS = Math.floor((Date.now() - _tokenIssuedAt) / 1000);
    console.log("[CoreFlow Dialer] Token is " + ageS + "s old, refreshing pre-call");
    try {
      await refreshToken("pre-call-age");
    } catch (e) {
      console.warn("[CoreFlow Dialer] Pre-call refresh failed, proceeding with old token:", e?.message);
      // Fall through — reactive recovery will catch if it fails.
    }
  }

  let cleanNumber = phoneNumber.replace(/[^\d+]/g, "");
  if (!cleanNumber.startsWith("+")) cleanNumber = "+1" + cleanNumber;

  console.log("[CoreFlow Dialer] Calling:", cleanNumber);
  emit("dialing", { to: cleanNumber });
  callStartTime = null;
  lastDialedNumber = cleanNumber;

  const callParams = {
    To: cleanNumber,
    orgId: window.__coreflow_org_id || "",
    selectedCallerId: window.__coreflow_selected_callerid || "",
  };

  // Extracted so the reactive-recovery retry path can re-wire events
  // onto the second call object. Identical to the previous inline
  // wire-up — just movable.
  const wireCallEvents = (call) => {
    call.on("accept", () => {
      callStartTime = Date.now();
      console.log("[CoreFlow Dialer] ✅ Call connected");
      emit("connected", { to: cleanNumber, startTime: callStartTime });
    });
    call.on("disconnect", () => {
      const duration = callStartTime ? Math.round((Date.now() - callStartTime) / 1000) : 0;
      const twilioSid = activeCall?.parameters?.CallSid || null;
      console.log("[CoreFlow Dialer] Call ended. Duration:", duration, "s");
      activeCall = null;
      callStartTime = null;
      emit("ended", { to: cleanNumber, duration, twilioSid });
    });
    call.on("error", async (err) => {
      console.error("[CoreFlow Dialer] Call error:", err.message, err.code);
      // Preserve existing behavior: emit callError so DialerPage logs a
      // failed-call row with full payload. Run this FIRST so the failure
      // is recorded regardless of what the recovery path does.
      emit("callError", {
        message: err.message,
        code: err.code,
        twilioSid: activeCall?.parameters?.CallSid || null,
        to: lastDialedNumber,
        duration: callStartTime ? Math.floor((Date.now() - callStartTime) / 1000) : 0,
      });
      // Token expired (31205) / invalid (31204) surfaced via the call's
      // async error channel — the path that the makeCall synchronous catch
      // never sees because device.connect() resolved successfully before
      // Twilio rejected on the server. Refresh + retry once. Shares the
      // _reactiveRetryInFlight guard with makeCall's synchronous-throw
      // path; whichever path acquires the flag leads, the other no-ops.
      const code = err?.code;
      const message = err?.message || "";
      const isTokenError = code === 31205 || code === 31204 || /token.*(expired|invalid)/i.test(message);
      if (isTokenError && !_reactiveRetryInFlight) {
        _reactiveRetryInFlight = true;
        try {
          console.log("[CoreFlow Dialer] Token error on call, refreshing + retrying");
          await refreshToken("call-error");
          // The "To" param is what we passed in makeCall's callParams.
          // customParameters is the SDK's Map of those values; fall back
          // to the lastDialedNumber module-scoped capture if missing.
          const retryNumber = call.customParameters?.get("To") || lastDialedNumber;
          if (retryNumber) {
            console.log("[CoreFlow Dialer] Retrying call to", retryNumber, "after token refresh");
            await makeCall(retryNumber);
          }
        } catch (e) {
          console.error("[CoreFlow Dialer] call-error refresh + retry failed:", e?.message);
        } finally {
          _reactiveRetryInFlight = false;
        }
      }
    });
    call.on("ringing", () => {
      console.log("[CoreFlow Dialer] 📞 Ringing...");
      emit("ringing", { to: cleanNumber });
    });
    call.on("reject", () => {
      activeCall = null;
      callStartTime = null;
      emit("ended", { to: cleanNumber, duration: 0, status: "no_answer" });
    });
  };

  try {
    // Defensive preflight — Joe reported "A Call is already active"
    // errors during Power Dialer auto-advance, plus brief audio overlap
    // where the previous call's voicemail kept playing as the next
    // call's UI loaded. Root cause: hangUp() (or the disconnect event
    // handler) sets our local activeCall=null synchronously, but the
    // SDK's WebRTC tear-down + internal call-object cleanup is async
    // (~50-500ms). If makeCall fires device.connect() inside that
    // window, Twilio rejects.
    //
    // Fix: force-disconnect any stale local activeCall, then poll
    // device.calls (the @twilio/voice-sdk v2 Map of active calls)
    // for up to 1s in 50ms slices until the SDK reports idle.
    // After 1s we proceed anyway — matches old behavior and prevents
    // an infinite loop if the SDK ever fails to drain.
    if (activeCall) {
      try { activeCall.disconnect(); } catch (e) {
        console.warn("[CoreFlow Dialer] Preflight disconnect of stale call failed:", e?.message);
      }
      activeCall = null;
    }
    let _waited = 0;
    while (_waited < 1000 && device.calls && device.calls.size > 0) {
      await new Promise((r) => setTimeout(r, 50));
      _waited += 50;
    }
    if (_waited >= 1000) {
      console.warn("[CoreFlow Dialer] Device still reports active call after 1s settle — proceeding anyway");
    }

    activeCall = await device.connect({ params: callParams });
    wireCallEvents(activeCall);
    return { success: true, to: cleanNumber };
  } catch (err) {
    // Reactive recovery: 31205 = JWTTokenExpiredError. Refresh the
    // token and retry connect() exactly once. Single retry cap +
    // in-flight flag prevents recursion or storms if the token
    // endpoint itself is down.
    const isTokenExpired = err?.code === 31205 || /token.*expired/i.test(err?.message || "");

    if (isTokenExpired && !_reactiveRetryInFlight) {
      console.warn("[CoreFlow Dialer] Call failed with expired token — refreshing and retrying once");
      _reactiveRetryInFlight = true;
      try {
        const refreshed = await refreshToken("reactive-recovery");
        if (refreshed) {
          try {
            activeCall = await device.connect({ params: callParams });
            wireCallEvents(activeCall);
            return { success: true, to: cleanNumber };
          } catch (retryErr) {
            console.error("[CoreFlow Dialer] Retry after token refresh also failed:", retryErr?.message);
            // fall through to the error-emit path below
          }
        }
      } finally {
        _reactiveRetryInFlight = false;
      }
    }

    console.error("[CoreFlow Dialer] Call failed:", err.message);
    emit("error", { message: err.message });
    // Also emit "ended" so DialerPage's onEnded handler runs and
    // logs a failed-call row. Without this, calls that throw at
    // device.connect() (no network, no mic, denied permission)
    // never reach the call-logging path at all.
    emit("ended", { twilioSid: null, to: lastDialedNumber, duration: 0, status: "failed" });
    return { success: false, error: err.message };
  }
  } finally {
    // Always release the in-flight flag — success, throw, or any return
    // path inside the inner try/catch. Without this a single thrown
    // error would jam the dialer permanently.
    _makeCallInFlight = false;
  }
}

// ─── Call Controls ───
export function hangUp() {
  if (activeCall) {
    activeCall.disconnect();
    activeCall = null;
  }
  return { success: true };
}

export function toggleMute() {
  if (!activeCall) return { muted: false };
  const newMuteState = !activeCall.isMuted();
  activeCall.mute(newMuteState);
  emit("mute", { muted: newMuteState });
  return { muted: newMuteState };
}

export function sendDigit(digit) {
  if (activeCall) activeCall.sendDigits(digit);
}

// Transfer the active call to another number
export async function transferCall(transferTo, mode = "cold") {
  if (!activeCall) {
    return { success: false, error: "No active call to transfer" };
  }

  try {
    // Get the Twilio CallSid from the active call
    const callSid = activeCall.parameters?.CallSid;
    if (!callSid) {
      return { success: false, error: "Cannot get CallSid from active call" };
    }

    console.log("[CoreFlow Dialer] Transferring call:", callSid, "to:", transferTo, "mode:", mode);

    const resp = await apiFetch("/api/twilio/transfer", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ callSid, transferTo, mode, orgId: window.__coreflow_org_id || "" }),
    });

    const data = await resp.json();

    if (!resp.ok) {
      console.error("[CoreFlow Dialer] Transfer failed:", data.error);
      return { success: false, error: data.error };
    }

    console.log("[CoreFlow Dialer] Transfer result:", data);

    // For cold transfer, the browser call will disconnect automatically
    if (mode === "cold") {
      activeCall = null;
      callStartTime = null;
      emit("ended", { transferredTo: transferTo, duration: getCallDuration() });
    }

    return { success: true, ...data };
  } catch (err) {
    console.error("[CoreFlow Dialer] Transfer error:", err.message);
    return { success: false, error: err.message };
  }
}

export function getActiveCallSid() {
  return activeCall?.parameters?.CallSid || null;
}

export function getCallDuration() {
  if (!callStartTime) return 0;
  return Math.round((Date.now() - callStartTime) / 1000);
}

export function getStatus() {
  return {
    deviceReady: device !== null && device.state === "registered",
    inCall: activeCall !== null,
    muted: activeCall ? activeCall.isMuted() : false,
    duration: getCallDuration(),
  };
}

export function destroyDevice() {
  if (device) {
    try { device.destroy(); } catch (e) { /* ignore */ }
    device = null;
  }
  activeCall = null;
  callStartTime = null;
  listeners = {};

  // Clear token-refresh state so a subsequent initDevice starts clean
  // and we don't leak the visibility listener or backstop timer across
  // teardowns.
  if (_backstopTimer) {
    clearTimeout(_backstopTimer);
    _backstopTimer = null;
  }
  if (_visibilityListenerAttached && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    _visibilityListenerAttached = false;
  }
  _identity = null;
  _tokenIssuedAt = null;
  _refreshInFlight = false;
  _reactiveRetryInFlight = false;
}

// ─── Incoming Call: Accept ───
export function acceptIncoming() {
  if (!activeCall) return;
  try {
    activeCall.accept();
    callStartTime = Date.now();

    activeCall.on("accept", () => {
      console.log("[CoreFlow Dialer] ✅ Incoming call accepted");
      emit("connected", { direction: "inbound" });
    });
    activeCall.on("disconnect", () => {
      const dur = getCallDuration();
      const fromNumber = activeCall?.parameters?.From || null;
      const twilioSid = activeCall?.parameters?.CallSid || null;
      console.log("[CoreFlow Dialer] Incoming call ended. Duration:", dur, "s");
      emit("ended", { duration: dur, direction: "inbound" });
      activeCall = null;
      callStartTime = null;

      // Log inbound call to call_logs so the Calls Received KPI populates.
      // Best-effort contact lookup by trailing 10 phone digits — failures
      // are non-fatal (NULL contact_id is a valid row). Identity comes
      // from window globals set during auth boot (see src/data/auth.js).
      const orgId = window.__coreflow_org_id;
      const userId = window.__coreflow_user_id;
      if (orgId && userId) {
        (async () => {
          try {
            let contactId = null;
            if (fromNumber) {
              const last10 = fromNumber.replace(/[^\d]/g, "").slice(-10);
              if (last10.length >= 7) {
                const { data } = await supabase.from("contacts")
                  .select("id").eq("org_id", orgId)
                  .ilike("phone", `%${last10}`).limit(1).maybeSingle();
                contactId = data?.id || null;
              }
            }
            await logCall({
              contactId, userId, orgId,
              phoneNumber: fromNumber || "",
              duration: dur,
              status: dur > 0 ? "completed" : "no_answer",
              twilioSid,
              direction: "inbound",
            });
          } catch (e) {
            console.warn("[CoreFlow Dialer] Inbound log failed:", e?.message);
          }
        })();
      }
    });
    activeCall.on("error", (err) => {
      console.error("[CoreFlow Dialer] Incoming call error:", err.message);
      emit("error", { message: err.message });
    });
  } catch (e) {
    console.error("[CoreFlow Dialer] Accept failed:", e.message);
  }
}

// ─── Incoming Call: Reject ───
export function rejectIncoming() {
  if (!activeCall) return;
  try {
    activeCall.reject();
    console.log("[CoreFlow Dialer] Incoming call rejected");
    emit("rejected", {});
    activeCall = null;
  } catch (e) {
    console.error("[CoreFlow Dialer] Reject failed:", e.message);
  }
}
